/**
 * Zelofin - Versioning Information
 * 
 * Semantic Versioning (semver): MAJOR.MINOR.PATCH
 * - MAJOR: Breaking changes / incompatible API changes
 * - MINOR: New features added in a backwards compatible manner
 * - PATCH: Bug fixes and small improvements
 */

// Versão do Zelofin
export const APP_VERSION = '6.0.30';

export const VERSION_INFO = {
  version: APP_VERSION,
  name: 'Zelofin MicroSaaS',
  description: 'Plataforma Multi-Tenant de Gestão Financeira com White Label - PF e PJ',
  
  // Build information
  buildDate: new Date().toISOString(),
  environment: import.meta.env.MODE || 'development',
  
  // Feature flags
  features: {
    // Features existentes v5.0.0
    bankSync: true,
    commitments: true,
    transfers: true,
    multiAccounts: true,
    dashboardExport: true,
    employeePhotos: true,
    imageCrop: true,
    payroll: true, // ✅ Folha de pagamento ATIVA e funcional (v3.0.0)
    calendarSync: true, // ✅ Sincronização inteligente da agenda (v3.0.0)
    
    // Features v6.0.0 - MicroSaaS
    multiTenancy: true, // ✅ Suporte a organizações multi-tenant
    organizations: true, // ✅ Criação e gestão de organizações PF/PJ
    subscriptionPlans: true, // ✅ Sistema de planos (Controle, Gestão, Domínio)
    permissions: true, // ✅ Sistema de permissões granular (roles + permissions)
    multipleUsers: true, // ✅ Múltiplos usuários por organização
    invitations: true, // ✅ Sistema de convites com tokens
    whiteLabel: false, // ⏳ White Label (logo + domínio) - Implementação pendente
    stripeIntegration: false, // ⏳ Integração Stripe - Implementação pendente
    customRoles: false, // ⏳ Custom Roles (Plano Domínio) - Implementação pendente
    backoffice: false, // ⏳ Painel Administrativo - Implementação pendente
  },
  
  // Changelog highlights for current version
  changelog: {
    '6.0.24': [
      '🚀 Início da implementação de importação de extrato com infraestrutura server-side',
      '🗄️ Novas migrações aditivas para sessões de importação, fila de revisão e metadados de deduplicação',
      '📥 Nova página Importar com fluxo inicial: upload, preview e confirmação para OFX/CSV',
    ],
    '6.0.8': [
      '⚡ Rotas de login, onboarding, relatórios compartilhados e telas autenticadas agora carregam sob demanda com React.lazy + Suspense',
      '📥 Exportação XLSX em Transactions foi movida para import dinâmico e deixou de pesar na navegação normal',
      '📦 Vite agora separa chunks de react, supabase, tanstack, radix e charts; o warning de chunk grande foi eliminado no build',
    ],
    '6.0.7': [
      '🔒 Corrigido o CSP do apps/web para permitir o carregamento controlado da tipografia Plus Jakarta Sans',
      '👀 Varredura visual confirmou a tela pública de autenticação sem regressões estruturais após o ajuste',
      '📦 Warning de chunk grande mapeado: rotas ainda carregam telas pesadas de forma eager, especialmente Transactions e Reports',
    ],
    '6.0.6': [
      '📈 Refino premium da página de Relatórios de Cartão com cards, gráficos e tabelas redesenhados',
      '🧠 Insights inteligentes receberam nova hierarquia visual, estados móveis aprimorados e bloco premium para análise com IA',
      '📊 Métricas, projeções, análise de antecipação e modal de compartilhamento agora seguem a mesma linguagem do novo Zelofin',
    ],
    '6.0.5': [
      '💼 Segundo rollout do design system premium com foco nas telas operacionais',
      '💸 Transactions ganhou novo header executivo, KPIs do período, leitura por natureza e histórico refinado',
      '🏦 Accounts recebeu resumo financeiro premium, cards de contas atualizados e formulário alinhado ao novo sistema visual',
      '🎯 Metas, Configurações Gerais e a shell de Relatórios agora seguem a mesma hierarquia visual do dashboard e da navegação principal',
    ],
    '6.0.4': [
      '🎨 Primeiro rollout do novo design system premium da Zelofin',
      '🟢 Nova base visual com paleta verde/dourado, tipografia Plus Jakarta Sans e sombras refinadas',
      '🧭 Shell renovado com sidebar colapsada em icones, header premium e hierarquia visual mais clara',
      '📊 Dashboard inicial redesenhado com novos KPIs, hero institucional, fluxo de caixa e graficos atualizados',
    ],
    '6.0.0': [
      '🚀 TRANSFORMAÇÃO MICROSAAS: Arquitetura Multi-Tenant Completa',
      '🏢 Suporte a Organizações PF (Pessoa Física) e PJ (Pessoa Jurídica)',
      '💳 3 Planos: Controle (R$29), Gestão (R$79), Domínio (R$199)',
      '👥 Sistema de múltiplos usuários por organização',
      '🔐 Sistema de permissões granular (owner, admin, employee, viewer)',
      '🎨 White Label: Logo personalizado + Domínio customizado',
      '📦 Arquitetura Monorepo com Turborepo',
      '🔧 Packages compartilhados: @zelofin/utils, @zelofin/permissions, @zelofin/database',
      '🗄️ 9 novas tabelas: organizations, roles, permissions, subscriptions',
      '💎 Integração Stripe (preparada)',
      '🎯 Sistema de convites com tokens',
      '📊 Limites por plano (usuários, contas, cartões, funcionários)',
      '🔍 Auditoria de acessos administrativos',
    ],
    '4.1.7': [
      '🗓️ FIX: Datas de vencimento de dívidas salvas sem deslocamento de timezone',
      '✅ Parcelas agora mantêm exatamente a data selecionada no modal',
      '📊 Sheet de parcelas exibe datas corretamente sem conversão de Date',
      '🔧 Nova função calculateInstallmentDueDate() para cálculo seguro de datas',
      '👥 FEAT: Funcionários desligados em lista colapsável (padrão de dívidas)',
      '📋 Painel de funcionários mais limpo e organizado',
    ],
    '4.1.6': [
      '🐛 CRITICAL FIX: current_occurrences agora sempre começa em 0',
      '✅ Corrigido valor inicial ao criar compromisso',
      '🔄 Sistema incrementa corretamente ao pagar fatura',
      '💾 Prevenção de bug: valores 24/24, 12/12 sem ter pago',
    ],
    '4.1.5': [
      '🐛 Corrigido exibição de repetições para compromissos de cartão',
      '✅ Status agora reflete valores do banco (current_occurrences)',
      '🔄 Sincronização em tempo real após pagar fatura',
      '📊 Prioridade para dados do DB em vez de cache local',
    ],
    '4.1.4': [
      '✅ Compromissos de cartão agora atualizam contador ao pagar fatura',
      '🔄 Sincronização automática: progresso 0/20 → 1/20 → 2/20...',
      '📊 Campo current_occurrences atualizado automaticamente',
      '🔙 Desfazer pagamento também atualiza contador corretamente',
    ],
    '4.1.3': [
      '⚡ Modal de sincronização abre instantaneamente (sem delay de 3s)',
      '✅ Corrigido 4 warnings de DOM nesting inválido do React',
      '🎨 Estrutura HTML agora válida e semântica',
    ],
    '4.1.2': [
      '🐛 Corrigido bug ao mudar método de pagamento de compromisso',
      '✅ Deleta lançamentos antigos de AMBAS as fontes (transactions + card_expenses)',
      '✅ Recria lançamentos na fonte correta conforme novo método',
      '🔄 Suporte completo para mudança conta → cartão e cartão → conta',
    ],
    '4.1.1': [
      '🐛 Corrigido bug ao editar compromissos com use_end_date=false',
      '✅ Lançamentos pendentes agora são recriados com novas configurações',
      '🔄 Melhorias na recriação de transações ao editar compromissos',
    ],
    '4.1.0': [
      '📚 ATUALIZAÇÃO MAJOR: Tutorial Completo de Compromissos',
      '✅ Corrigido item sobre exclusão (não deleta transações pagas)',
      '🔄 Adicionada documentação sobre botão Sincronizar',
      '⚙️ Adicionada documentação sobre Reconstruir Lançamentos',
      '💡 Novos exemplos práticos de sincronização e exclusão',
      '🎯 Tutorial 100% alinhado com implementação real',
      '✨ Loading states em estatísticas de compromissos',
    ],
    '3.3.7': [
      '🐛 Corrigido suporte completo para cartão de crédito',
      '✅ Adicionada verificação de duplicatas',
      '🔧 Melhorias no CommitmentCardService',
    ],
    '3.2.1': [
      '🐛 Corrigido indicador de pendências no dashboard',
      '✅ Agora mostra apenas pendências do mês atual',
      '🎯 Removida inclusão de meses adjacentes no filtro',
    ],
    '3.2.0': [
      '✨ Correção completa dos cálculos da folha de pagamento',
      '🔧 Ajustes nos descontos e vale transporte',
      '📊 Melhorias no painel de simulação',
    ],
    '3.0.0': [
      '� NOME ATUALIZADO: Zelofin+ → Zelofin (removido o +)',
      '📑 Títulos dinâmicos nas abas: "Página | Zelofin"',
      '�🎉 SISTEMA DE FOLHA DE PAGAMENTO ATIVO E FUNCIONAL',
      '✨ NOVO: Botão Sincronizar na Agenda Financeira com timestamp',
      '🔄 Auto-refresh ao entrar na aba Calendar (refetchOnMount: always)',
      '💫 Animações suaves durante sincronização (mínimo 1.5s)',
      '⏰ Display de "última atualização" com tempo relativo',
      '🎨 Efeitos visuais: spin, pulse, shadow durante loading',
      '🚀 Sincronização inteligente: F5, botão manual ou troca de aba',
      '💼 Folha de pagamento integrada à Agenda Financeira',
      '📊 Transações de folha aparecem automaticamente na agenda',
      '👥 Gestão completa de funcionários e itens variáveis',
    ],
    '2.20.11': [
      'Implementação inicial do botão sincronizar',
      'Sistema de timestamp com localStorage',
      'Integração com TanStack Query',
    ],
    '2.17.16': [
      'Corrigido cálculo do vale: agora 40% do salário BRUTO (antes era do líquido)',
      'Adicionado cálculo automático de último dia útil para pagamentos',
      'Detecta e respeita feriados nacionais brasileiros',
      'Detecta e evita sábados e domingos para datas de pagamento',
      'Exibição das datas reais de pagamento (vale e final) na simulação',
      'Vale sempre pago no dia 15 (ou último dia útil anterior)',
      'Final sempre pago no dia 30 (ou último dia útil anterior)',
    ],
    '2.12.2': [
      'Path fixo: {user_id}/{employee_id}/profile.jpg',
      'Upload com upsert (sempre sobrescreve anterior)',
      'Migração SQL automatizada (bucket + RLS policies)',
      'Elimina acúmulo de fotos antigas',
    ],
    '2.12.1': [
      'Upload de foto de perfil para funcionários',
      'Componente com drag & drop e seleção via botão',
      'Preview circular (Avatar 128x128px) com iniciais como fallback',
      'Validação client-side (JPG/PNG/WebP, max 2MB)',
      'Compressão automática para imagens > 500KB',
      'Upload/remoção imediatos para funcionários existentes',
      'Cleanup automático de fotos antigas ao trocar',
      'RLS Policies para segurança por usuário',
      'Path structure segura: {user_id}/{employee_id}/{timestamp}-{filename}',
    ],
    '2.5.0': [
      'Adicionada funcionalidade de exportação do Dashboard',
      'Botão "Exportar" no canto superior direito do Dashboard',
      'Menu dropdown com 3 opções de exportação',
      'Exportação em Imagem JPG (liberada e funcional)',
      'Exportação em Documento PDF (em breve)',
      'Exportação via Link Compartilhado (em breve)',
      'Imagem gerada em alta qualidade (2x pixel ratio)',
      'Nome do arquivo com mês/ano da visualização',
      'Exclusão automática de menus e navegação na exportação',
    ],
    '2.4.1': [
      'Adicionada marcação de progresso nas transações de compromissos (ex: "5/24")',
      'Transações fixas agora mostram ocorrência atual/total na descrição',
      'Compromissos percentuais mostram o percentual na descrição (ex: "10%")',
      'FIX CRÍTICO: Corrigido bug de double increment no script de geração',
      'Bug causava criação de apenas 6 transações em vez de 12',
      'Bug causava numeração incorreta (1/12, 3/12, 5/12... pulando números)',
      'Script SQL para atualizar descrições existentes com marcação de progresso',
      'Script SQL para deletar transações malformadas e regenerar',
      'Corrigido erro no script fix-existing: removido campo "updated_at" inexistente',
      'Melhorado rastreamento de progresso em compromissos recorrentes',
      'Proteção contra duplicação de marcação em descrições',
    ],
    '2.4.0': [
      'Adicionado Sheet expansível para detalhes de Cofrinhos',
      'Adicionado Sheet expansível para detalhes de Compromissos',
      'Gráfico "Gastos por Categoria" agora ordena por top 10 valores (maior → menor)',
      'Progress bars com cores individuais para cada cofrinho',
      'Tracking de pagamentos em compromissos (pagas/pendentes)',
      'Indicadores de progresso para ocorrências de compromissos',
      'Cards de Cofrinhos e Compromissos agora são clicáveis',
      'Interface aprimorada com ícone Target para melhor visualização',
    ],
    '2.3.1': [
      'Corrigido cálculo de receitas: saldo inicial de contas agora é incluído',
      'Contas criadas no mês têm seu saldo_inicial contabilizado como receita',
      'Apenas no mês de criação da conta o saldo inicial é considerado',
      'Card de Receita Total mostra detalhamento: transações + saldo inicial',
      'Indicador visual mostra quantidade de contas criadas e valor do saldo inicial',
      'Corrige problema de saldo negativo quando conta tem saldo inicial',
      'Logging aprimorado para debug do cálculo (transactionIncome vs initialBalanceIncome)',
    ],
    '2.3.0': [
      'Adicionada diferenciação entre lançamentos fixos (compromissos) e variáveis',
      'Nova coluna "Natureza" na tabela de lançamentos com badges visuais',
      'Filtro de natureza: Fixa (compromisso) vs Variável',
      'Badge laranja para lançamentos fixos (compromissos automáticos)',
      'Badge azul para lançamentos variáveis (manuais)',
      'Campo commitment_id adicionado à tabela transactions',
      'Migração SQL para relacionar transações com compromissos',
      'Interface atualizada com filtros combinados',
    ],
    '2.2.1': [
      'Corrigido bug crítico: Gastos de cartão não são mais contados em duplicidade',
      'Corrigido: Receita Total agora considera apenas transações com status "paid"',
      'Corrigido: Transações pendentes e canceladas não afetam mais os totais',
      'Corrigido: Movimentos de cofrinho não são mais incluídos nos cálculos',
      'Adicionado contador de transações nos cards de Receita e Despesa',
      'Adicionado indicador de valores pendentes (quando existirem)',
      'Melhorado logging para debug dos cálculos financeiros',
      'Separada lógica de cálculo: transactions vs card_expenses',
      'Gráfico de categorias agora combina corretamente transactions + card_expenses',
    ],
    '2.2.0': [
      'Redesenhado Dashboard com nova UI moderna e clean',
      'Criado componente MonthSelector elegante com calendário interativo',
      'Corrigido bug crítico: filtro de mês agora mostra o mês correto (estava mostrando outubro ao invés de novembro)',
      'Melhorado layout do cabeçalho com título maior e mais destaque',
      'Removido input nativo de mês (feio) substituído por componente customizado',
      'Adicionado indicador visual "Atual" para o mês corrente',
      'Melhorada navegação entre meses com atalhos de teclado (Ctrl + setas)',
      'Grid de meses com 3 colunas para seleção rápida',
      'Navegação de ano integrada no seletor',
      'Indicador visual sutil para o mês atual no grid',
    ],
    '1.3.5': [
      'Otimizada responsividade mobile para prevenir zoom horizontal necessário',
      'Melhorado espaçamento e sizing de elementos para telas pequenas',
      'Ajustado floating action bar para mobile com posicionamento fixo correto',
      'Reduzido tamanho de fontes e elementos para melhor fit em mobile',
      'Corrigido overflow horizontal em cards e modais',
    ],
    '1.3.4': [
      'Adicionado layout responsivo mobile para página de Expenses (gastos de cartão)',
      'Interface móvel com cards compactos substituindo tabela horizontal',
      'Melhorada navegação mobile com botões otimizados para toque',
      'Floating action bar responsiva para operações em lote',
      'Preservada funcionalidade desktop mantendo tabela original',
    ],
    '1.0.3': [
      'Melhorada interface da modal de transações com layout mais compacto',
      'Adicionado botão "Continuar adicionando" com melhor destaque visual',
      'Modal agora se adapta melhor a telas menores com scroll interno',
    ],
    '1.0.2': [
      'Corrigido erro "invalid input syntax for type uuid" ao salvar transações',
      'Melhorada validação de campos UUID para aceitar valores vazios como null',
      'Corrigido problema com strings vazias em campos opcionais do banco de dados',
    ],
    '1.0.1': [
      'Corrigido bug nos campos de descrição que impedia espaços durante digitação',
      'Melhorada sanitização de entrada para preservar experiência do usuário',
      'Sanitização agora só remove espaços extras no momento do salvamento',
    ],
    '1.0.0': [
      'Sistema básico de transações (receitas/despesas)',
      'Integração com contas bancárias',
      'Sistema de categorias padrão e personalizadas', 
      'Cálculo automático de dízimos',
      'Sistema de compromissos financeiros',
      'Transferências entre contas',
      'Dashboard de saldos por conta',
      'Sincronização automática com bancos',
    ]
  }
};

// Export individual values for easier imports
export const { version, name, description } = VERSION_INFO;
export const { features } = VERSION_INFO;
